import React, { useState } from 'react';
import styles from './styles.css';
import clearStyles from '../SubjectPickerMenu/SubComponents/ShowClearButtons/styles.css';
import { default as LoadingSpinner } from '../LoadingSpinner/index.jsx';
import { default as CustomGroupsManager } from '../CustomGroupsManager/index.jsx';
import { useTranslation } from 'react-i18next';
import customGroupsService from '../../services/customGroupsService';
import { isACountry } from '../../actions';
// Rendering bare names in the location menu (no LocationName wrapper)

export const isAllChildrensSelected = (childrens, selectedSubjects) => {
	const safeChildren = childrens || [];
	const safeSelected = selectedSubjects || [];
	const isAllSubjectSelected = safeChildren.every((child) => {
		if (!safeSelected.includes(child)) {
			return false;
		}
		return true;
	});
	return isAllSubjectSelected;
};

function ArrowIcon({ className }) {
	return (
		<svg
			className={className}
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
		</svg>
	);
}

export default function LocationPickerMenu({
	description,
	buttons,
	countryList,
	groupList,
	selectedLocations,
	onLocationSelect,
	onAllGroupsSelect,
	onAllCountriesSelect,
	isSimplifiedView,
	show,
}) {
	const { t } = useTranslation();
	const [search, setSearch] = useState('');

	const handleCustomGroupSelect = (customGroupParam, shouldAdd) => {
		let locationsToSet;
		if (shouldAdd) {
			// Add to selection
			locationsToSet = [...selectedLocations, customGroupParam];
		} else {
			// Remove from selection
			const groupId = customGroupsService.urlUtils.parseUrlParam(customGroupParam)?.id;
			locationsToSet = selectedLocations.filter(
				(loc) => customGroupsService.urlUtils.parseUrlParam(loc)?.id !== groupId,
			);
		}

		// Use the proper onLocationSelect callback
		onLocationSelect(locationsToSet.join('-'));
	};

	const handleCustomGroupUpdate = (oldGroupId, newGroupId, newGroupName, newCountries) => {
		// Check if the old group is currently selected
		const isCurrentlySelected = selectedLocations.some((loc) => {
			const parsed = customGroupsService.urlUtils.parseUrlParam(loc);
			return parsed && parsed.id === oldGroupId;
		});

		if (isCurrentlySelected) {
			// Replace the old group with the new one in the URL
			const newCustomGroupParam = customGroupsService.urlUtils.generateUrlParam(newGroupId, newGroupName, newCountries);
			const updatedLocations = selectedLocations.map((loc) => {
				const parsed = customGroupsService.urlUtils.parseUrlParam(loc);
				if (parsed && parsed.id === oldGroupId) {
					return newCustomGroupParam;
				}
				return loc;
			});
			onLocationSelect(updatedLocations.join('-'));
		}
	};

	if (isSimplifiedView) {
		return (
			<div className={styles.appMenu}>
				<div className={styles.menuContainer}>
					<div className={styles.menuHeader}>
						<div>
							<p className={styles.menuDescription}>{description}</p>
						</div>
					</div>
					<div className="uk-container icon-container"></div>
					<div className={styles.menuContentWrapperGrid}>
						<div className={styles.countriesSectionBox}>
							<div className={styles.countriesColumnGrid}>
								<div className={styles.columnTitle}>{t('Countries')}</div>
								<div className={styles.countriesGrid}>
									{(Array.isArray(countryList) ? countryList : []).map((country, index) => (
										<a
											key={country.iso}
											href={`/locations/${country.iso.toLowerCase()}.html`}
											className={styles.squareButton}
										>
											<img
												className={styles.flagIcon}
												src={`/img/roundflags/${country.iso.toLowerCase()}.svg`}
												alt={country.fullName}
											/>
											<span className={styles.squareText}>{country.fullName}</span>
										</a>
									))}
								</div>
							</div>
						</div>
						<div className={styles.groupsSectionBox}>
							<div className={styles.groupsColumnGrid}>
								<div className={styles.columnTitle}>{t('Groups of countries')}</div>
								<div className={styles.groupsListGrid}>
									{(Array.isArray(groupList) ? groupList : []).map((group, index) => (
										<a
											key={group.uid}
											href={`/locations/${group.sh_nm.toLowerCase()}.html`}
											className={styles.squareButton}
										>
											{group.sh_nm === 'Africa' && (
												<img src="/img/icons/africa-outline.svg" alt="" className={styles.groupIcon} />
											)}
											<span className={styles.squareText}>{group.fullName}</span>
										</a>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	const listOfAllCountries = Array.isArray(countryList) ? countryList.map((country) => country.iso) : [];
	const listOfAllGroups = Array.isArray(groupList) ? groupList.map((group) => group.uid) : [];

	const [isAllCountriesSelected, selectAllCountries] = useState(
		isAllChildrensSelected(listOfAllCountries, selectedLocations),
	);
	const [isAllGroupsSelected, selectAllGroups] = useState(isAllChildrensSelected(listOfAllGroups, selectedLocations));

	const filteredCountries = search
		? (Array.isArray(countryList) ? countryList : []).filter((country) =>
				country.fullName.toLowerCase().includes(search.toLowerCase()),
			)
		: Array.isArray(countryList)
			? countryList
			: [];
	const filteredGroups = search
		? (Array.isArray(groupList) ? groupList : []).filter((group) =>
				group.fullName.toLowerCase().includes(search.toLowerCase()),
			)
		: Array.isArray(groupList)
			? groupList
			: [];

	if (!show) return null;

	return (
		<div className={`app-menu country-menu`}>
			<div className="uk-container icon-container"></div>
			<div className="app-menu-inner">
				<div className={styles.menuHeader}>
					<p className="menu-description">{description}</p>
					<div className="search-container">
						<input
							type="text"
							placeholder={t('Search locations or groups')}
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="search"
							aria-label={t('Search locations or groups')}
						/>
						<span className="search-icon">
							<svg width="16" height="16" viewBox="0 0 20 20">
								<circle fill="none" stroke="currentColor" strokeWidth="1.5" cx="9" cy="9" r="7" />
								<path fill="none" stroke="currentColor" strokeWidth="1.5" d="M14 14l5 5" />
							</svg>
						</span>
					</div>
					{!isSimplifiedView && Array.isArray(buttons) && buttons.length > 0 ? (
						<div className="app-menu-showclear location-showclear">
							{buttons.map((button, index) => {
								if (button.visible?.() === false) {
									return null;
								}
								return (
									<a
										key={`${button.label?.toString().split(' ').join('') || 'btn'}_${index}`}
										href="#"
										className={`${styles.menuControlButton} ${button.class === 'clear-comparison' ? clearStyles.clearButton : ''} ${button.class === 'show-comparison' ? clearStyles.showButton : ''}`}
										onClick={(e) => {
											e.preventDefault();
											if (typeof button.onClick === 'function') {
												button.onClick();
											}
										}}
									>
										{button.label}
										{button.class === 'clear-comparison' && (
											<svg
												className={clearStyles.clearIcon}
												viewBox="0 0 12 12"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
											</svg>
										)}
									</a>
								);
							})}
						</div>
					) : null}
				</div>
				<div className={styles.menuContentWrapperGrid}>
					<div className={styles.countriesSectionBox}>
						<div className={styles.countriesColumnGrid}>
							<div className={styles.columnTitle}>{t('Countries')}</div>
							<div className={styles.countriesGrid}>
								<button
									type="button"
									className={`${styles.squareButton} ${styles.allButton} ${isAllChildrensSelected(listOfAllCountries, selectedLocations) ? styles.selected : ''}`}
									onClick={() => {
										onAllCountriesSelect(isAllChildrensSelected(listOfAllCountries, selectedLocations));
										selectAllCountries(!isAllCountriesSelected);
									}}
									aria-label={t('Select all countries')}
								>
									{t('ALL COUNTRIES')}
								</button>
								{filteredCountries.map((country, index) => {
									const isSelected = selectedLocations.includes(country.iso);
									return (
										<button
											key={country.iso}
											type="button"
											className={`${styles.squareButton} ${isSelected ? styles.selected : ''}`}
											onClick={() => {
												let locationsToSet;
												if (isSelected) {
													locationsToSet = selectedLocations.filter((location) => location !== country.iso);
												} else {
													locationsToSet = [...selectedLocations, country.iso];
												}
												onLocationSelect(locationsToSet.join('-'));
											}}
											aria-pressed={isSelected}
											aria-label={
												isSelected
													? t('Deselect {{country}}', { country: country.fullName })
													: t('Select {{country}}', { country: country.fullName })
											}
											tabIndex={0}
										>
											<img
												className={styles.flagIcon}
												src={`/img/roundflags/${country.iso.toLowerCase()}.svg`}
												alt={country.fullName}
											/>
											<span className={styles.squareText}>{country.fullName}</span>
										</button>
									);
								})}
							</div>
						</div>
					</div>
					<div className={styles.groupsSectionBox}>
						<div className={styles.groupsColumnGrid}>
							<div className={styles.columnTitle}>{t('Groups of countries')}</div>
							<div className={styles.groupsListGrid}>
								<button
									type="button"
									className={`${styles.squareButton} ${styles.allButton} ${isAllChildrensSelected(listOfAllGroups, selectedLocations) ? styles.selected : ''}`}
									onClick={() => {
										onAllGroupsSelect(isAllChildrensSelected(listOfAllGroups, selectedLocations));
										selectAllGroups(!isAllGroupsSelected);
									}}
									aria-label={t('Select all groups')}
								>
									{t('ALL GROUPS')}
								</button>
								{filteredGroups.map((group, index) => {
									const isSelected = selectedLocations.includes(group.uid);
									return (
										<button
											key={group.uid}
											type="button"
											className={`${styles.squareButton} ${isSelected ? styles.selected : ''}`}
											onClick={() => {
												let locationsToSet;
												if (isSelected) {
													locationsToSet = selectedLocations.filter((location) => location !== group.uid);
												} else {
													locationsToSet = [...selectedLocations, group.uid];
												}
												onLocationSelect(locationsToSet.join('-'));
											}}
											aria-pressed={isSelected}
											aria-label={
												isSelected
													? t('Deselect {{group}}', { group: group.fullName })
													: t('Select {{group}}', { group: group.fullName })
											}
											tabIndex={0}
										>
											{group.sh_nm === 'Africa' && (
												<img src="/img/icons/africa-outline.svg" alt="" className={styles.groupIcon} />
											)}
											<span className={styles.squareText}>{group.fullName}</span>
										</button>
									);
								})}
							</div>
							<CustomGroupsManager
								countryList={countryList}
								selectedLocations={selectedLocations}
								onGroupSelect={handleCustomGroupSelect}
								onGroupUpdate={handleCustomGroupUpdate}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
